import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import WorkLogChart from '../../components/WorkLogChart';

function todayLocalString() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function currentMonthString() {
  return todayLocalString().slice(0, 7);
}

function to12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  let hours = parseInt(h, 10);
  const modifier = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${modifier}`;
}

function timeRange(startTime, endTime) {
  return `${to12h(startTime)}–${to12h(endTime)}`;
}

const CATEGORY_BAR_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6', '#EF4444'];

const STATUS_BADGE = {
  pending: 'bg-gray-100 text-gray-700',
  in_process: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};

function StatTile({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-bold text-gray-900">{value}</p>
    </div>
  );
}

function TasksTable({ tasks }) {
  if (tasks.length === 0) {
    return <p className="py-4 text-center text-xs text-gray-400">No work log entries this month.</p>;
  }
  return (
    <div className="max-h-72 overflow-y-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-white">
          <tr className="text-gray-500">
            <th className="pb-2 pr-2 font-semibold">Date</th>
            <th className="pb-2 pr-2 font-semibold">Title</th>
            <th className="pb-2 pr-2 font-semibold">Category</th>
            <th className="pb-2 pr-2 font-semibold">Time</th>
            <th className="pb-2 pr-2 font-semibold">Hours</th>
            <th className="pb-2 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={t.workLogId} className="border-t border-gray-100">
              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-600">{t.date}</td>
              <td className="py-1.5 pr-2 text-gray-900">{t.title}</td>
              <td className="py-1.5 pr-2 text-gray-600">{t.categoryName}</td>
              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-600">
                {t.startTime ? timeRange(t.startTime, t.endTime) : '—'}
              </td>
              <td className="py-1.5 pr-2 text-gray-600">{t.durationHours ?? '—'}</td>
              <td className="py-1.5">
                {t.status ? (
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_BADGE[t.status] || STATUS_BADGE.pending}`}>
                    {t.status.replace('_', ' ')}
                  </span>
                ) : (
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700">planned</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SchedulesTable({ schedules }) {
  if (schedules.length === 0) {
    return <p className="py-4 text-center text-xs text-gray-400">No scheduled classes this month.</p>;
  }
  return (
    <div className="max-h-72 overflow-y-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-white">
          <tr className="text-gray-500">
            <th className="pb-2 pr-2 font-semibold">Date</th>
            <th className="pb-2 pr-2 font-semibold">Subject</th>
            <th className="pb-2 pr-2 font-semibold">Batch</th>
            <th className="pb-2 pr-2 font-semibold">Room</th>
            <th className="pb-2 font-semibold">Time</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map((s) => (
            <tr key={s.scheduleId} className="border-t border-gray-100">
              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-600">{s.date}</td>
              <td className="py-1.5 pr-2 text-gray-900">{s.subjectName || '—'}</td>
              <td className="py-1.5 pr-2 text-gray-600">{s.batchName || '—'}</td>
              <td className="py-1.5 pr-2 text-gray-600">{s.classroomName || '—'}</td>
              <td className="py-1.5 whitespace-nowrap text-gray-600">
                {s.startTime ? timeRange(s.startTime, s.endTime) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventsTable({ events }) {
  if (events.length === 0) {
    return <p className="py-4 text-center text-xs text-gray-400">No events this month.</p>;
  }
  return (
    <div className="max-h-72 overflow-y-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-white">
          <tr className="text-gray-500">
            <th className="pb-2 pr-2 font-semibold">Date</th>
            <th className="pb-2 pr-2 font-semibold">Event</th>
            <th className="pb-2 pr-2 font-semibold">Type</th>
            <th className="pb-2 pr-2 font-semibold">Room</th>
            <th className="pb-2 font-semibold">Time</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.eventId} className="border-t border-gray-100">
              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-600">{e.date}</td>
              <td className="py-1.5 pr-2 text-gray-900">
                {e.title}
                {e.role && <span className="ml-1 text-gray-400">({e.role})</span>}
              </td>
              <td className="py-1.5 pr-2 text-gray-600">{e.eventTypeName || '—'}</td>
              <td className="py-1.5 pr-2 text-gray-600">{e.classroomName || '—'}</td>
              <td className="py-1.5 whitespace-nowrap text-gray-600">
                {e.startTime ? timeRange(e.startTime, e.endTime) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ROSTER_DAYTYPE_META = {
  'off day': { label: 'Off day', badge: 'bg-red-100 text-red-700' },
  'morning half day': { label: 'Morning half day', badge: 'bg-amber-100 text-amber-700' },
  'evening half day': { label: 'Evening half day', badge: 'bg-amber-100 text-amber-700' },
};

function RosterTable({ roster }) {
  if (roster.length === 0) {
    return <p className="py-4 text-center text-xs text-gray-400">No roster data for this month.</p>;
  }
  return (
    <div className="max-h-72 overflow-y-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-white">
          <tr className="text-gray-500">
            <th className="pb-2 pr-2 font-semibold">Date</th>
            <th className="pb-2 pr-2 font-semibold">Day</th>
            <th className="pb-2 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {roster.map((r) => {
            const meta = ROSTER_DAYTYPE_META[r.dayType];
            return (
              <tr key={r.date} className="border-t border-gray-100">
                <td className="py-1.5 pr-2 whitespace-nowrap text-gray-600">{r.date}</td>
                <td className="py-1.5 pr-2 text-gray-600">{r.day}</td>
                <td className="py-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${meta ? meta.badge : 'bg-gray-100 text-gray-600'}`}>
                    {meta ? meta.label : 'Normal working day'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const LEAVE_TYPE_LABEL = {
  leave: 'Leave (full day)',
  'morning half day leave': 'Morning half day leave',
  'evening half day leave': 'Evening half day leave',
  unavailability: 'Unavailability',
};

function LeavesTable({ leaves }) {
  if (leaves.length === 0) {
    return <p className="py-4 text-center text-xs text-gray-400">No leave or unavailability records this month.</p>;
  }
  return (
    <div className="max-h-72 overflow-y-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-white">
          <tr className="text-gray-500">
            <th className="pb-2 pr-2 font-semibold">Date</th>
            <th className="pb-2 pr-2 font-semibold">Type</th>
            <th className="pb-2 pr-2 font-semibold">Time</th>
            <th className="pb-2 font-semibold">Reason</th>
          </tr>
        </thead>
        <tbody>
          {leaves.map((l) => (
            <tr key={l.id} className="border-t border-gray-100">
              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-600">{l.date}</td>
              <td className="py-1.5 pr-2 text-gray-900">{LEAVE_TYPE_LABEL[l.type] || l.type}</td>
              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-600">
                {l.startTime ? timeRange(l.startTime, l.endTime) : 'Full day'}
              </td>
              <td className="py-1.5 text-gray-600">{l.reason || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StaffProductivityCard({ staff, month, serverToday, onMonthChange }) {
  const maxCategoryHours = Math.max(1, ...staff.categoryBreakdown.map((c) => c.hours));

  const [showDetails, setShowDetails] = useState(false);
  const [detailTab, setDetailTab] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [events, setEvents] = useState([]);
  const [roster, setRoster] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadDetails = useCallback(async () => {
    setLoadingDetails(true);
    try {
      const [tasksRes, schedulesRes, eventsRes, rosterRes, leavesRes] = await Promise.all([
        axiosInstance.get(`/admin/staff-productivity/${staff.staffId}/tasks`, { params: { month } }),
        axiosInstance.get(`/admin/staff-productivity/${staff.staffId}/schedules`, { params: { month } }),
        axiosInstance.get(`/admin/staff-productivity/${staff.staffId}/events`, { params: { month } }),
        axiosInstance.get(`/admin/staff-productivity/${staff.staffId}/roster`, { params: { month } }),
        axiosInstance.get(`/admin/staff-productivity/${staff.staffId}/leaves`, { params: { month } }),
      ]);
      setTasks(tasksRes.data?.tasks || []);
      setSchedules(schedulesRes.data?.schedules || []);
      setEvents(eventsRes.data?.events || []);
      setRoster(rosterRes.data?.roster || []);
      setLeaves(leavesRes.data?.leaves || []);
    } catch {
      setTasks([]);
      setSchedules([]);
      setEvents([]);
      setRoster([]);
      setLeaves([]);
    } finally {
      setLoadingDetails(false);
    }
  }, [staff.staffId, month]);

  useEffect(() => {
    if (showDetails) loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetails, month]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {staff.imagePath ? (
          <img src={getImageURL(staff.imagePath)} alt={staff.name} className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700">
            {(staff.name || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate font-semibold text-gray-900">{staff.name}</p>
          <p className="truncate text-xs text-gray-500">
            {staff.positionName || '—'}{staff.employeeNumber ? ` · #${staff.employeeNumber}` : ''}
          </p>
        </div>
        {staff.needsAttentionCount > 0 && (
          <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">
            {staff.needsAttentionCount} pending/in-progress
          </span>
        )}
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="ml-auto shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
        >
          {showDetails ? 'Hide details' : 'View details'}
        </button>
      </div>

      <WorkLogChart month={month} days={staff.days} today={serverToday} onMonthChange={onMonthChange} />

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Expected" value={`${staff.totalExpectedHours}h`} />
        <StatTile label="Actual" value={`${staff.totalActualHours}h`} />
        <StatTile label="Utilization" value={staff.utilizationPct == null ? '—' : `${staff.utilizationPct}%`} />
        <StatTile
          label="Completed · Pending · In-progress"
          value={`${staff.statusCounts.completed} · ${staff.statusCounts.pending} · ${staff.statusCounts.in_process}`}
        />
      </div>

      {staff.categoryBreakdown.length > 0 && (
        <div className="mt-4">
          <p className="mb-1.5 text-xs font-semibold text-gray-500">Time by category</p>
          <div className="space-y-1.5">
            {staff.categoryBreakdown.map((c, idx) => (
              <div key={c.categoryName} className="flex items-center gap-2 text-xs">
                <span className="w-28 shrink-0 truncate text-gray-600">{c.categoryName}</span>
                <div className="h-2 flex-1 rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${Math.max(4, (c.hours / maxCategoryHours) * 100)}%`,
                      backgroundColor: CATEGORY_BAR_COLORS[idx % CATEGORY_BAR_COLORS.length],
                    }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right text-gray-500">{c.hours}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showDetails && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="mb-3 flex flex-wrap items-center gap-1 rounded-lg bg-gray-100 p-1 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setDetailTab('tasks')}
              className={`rounded-md px-3 py-1.5 ${detailTab === 'tasks' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
            >
              Tasks ({tasks.length})
            </button>
            <button
              type="button"
              onClick={() => setDetailTab('schedules')}
              className={`rounded-md px-3 py-1.5 ${detailTab === 'schedules' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
            >
              Schedules ({schedules.length})
            </button>
            <button
              type="button"
              onClick={() => setDetailTab('events')}
              className={`rounded-md px-3 py-1.5 ${detailTab === 'events' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
            >
              Events ({events.length})
            </button>
            <button
              type="button"
              onClick={() => setDetailTab('roster')}
              className={`rounded-md px-3 py-1.5 ${detailTab === 'roster' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
            >
              Roster
            </button>
            <button
              type="button"
              onClick={() => setDetailTab('leaves')}
              className={`rounded-md px-3 py-1.5 ${detailTab === 'leaves' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
            >
              Leaves ({leaves.length})
            </button>
          </div>
          {loadingDetails ? (
            <p className="py-4 text-center text-xs text-gray-400">Loading…</p>
          ) : detailTab === 'tasks' ? (
            <TasksTable tasks={tasks} />
          ) : detailTab === 'schedules' ? (
            <SchedulesTable schedules={schedules} />
          ) : detailTab === 'events' ? (
            <EventsTable events={events} />
          ) : detailTab === 'roster' ? (
            <RosterTable roster={roster} />
          ) : (
            <LeavesTable leaves={leaves} />
          )}
        </div>
      )}
    </div>
  );
}

export default function StaffProductivity() {
  const [month, setMonth] = useState(currentMonthString());
  const [staffList, setStaffList] = useState([]);
  const [serverToday, setServerToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadSummary = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/staff-productivity/summary', { params: { month: m } });
      setStaffList(res.data?.staff || []);
      setServerToday(res.data?.serverToday || null);
    } catch {
      setStaffList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(month); }, [month, loadSummary]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staffList;
    return staffList.filter(
      (s) => s.name?.toLowerCase().includes(q) || s.employeeNumber?.toLowerCase().includes(q)
    );
  }, [staffList, search]);

  return (
    <DashboardLayout title="Staff Productivity">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Staff Productivity</h1>
        <p className="mt-1 text-sm text-gray-600">
          Monthly working hours, utilization, and category breakdown for every staff member assigned to a course.
        </p>
      </div>

      <div className="mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or employee number…"
          className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : filteredStaff.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
          {staffList.length === 0
            ? 'No staff are currently assigned to an ongoing course.'
            : 'No staff match your search.'}
        </div>
      ) : (
        <div className="space-y-6">
          {filteredStaff.map((staff) => (
            <StaffProductivityCard
              key={staff.staffId}
              staff={staff}
              month={month}
              serverToday={serverToday}
              onMonthChange={setMonth}
            />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
